import React from 'react'

/**
 * Split free text into plain segments and clickable hashtag segments so users
 * can tap a `#tag` to jump to its feed. Unicode-aware so Devanagari/Marathi tags
 * work. The tag is passed to {@link onTagClick} WITHOUT the leading `#`.
 */
const HASHTAG_RE = /(^|\s)(#[\p{L}\p{N}\p{M}_]+)/gu

export interface HashtagTextProps {
  content: string
  onTagClick: (tag: string) => void
  className?: string
  tagClassName?: string
}

export const HashtagText: React.FC<HashtagTextProps> = ({
  content,
  onTagClick,
  className,
  tagClassName
}) => {
  if (!content) return null

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null

  // Clone with global flag intact (stateful lastIndex).
  const re = new RegExp(HASHTAG_RE.source, HASHTAG_RE.flags)
  while ((match = re.exec(content)) !== null) {
    const prefix = match[1] // leading whitespace (or start-of-string empty)
    const tag = match[2] // includes leading '#'
    const matchStart = match.index

    if (matchStart > lastIndex) {
      parts.push(content.slice(lastIndex, matchStart))
    }
    if (prefix) parts.push(prefix)
    parts.push(
      <button
        key={`tag-${key++}`}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onTagClick(tag.slice(1))
        }}
        className={tagClassName ?? 'text-blue-600 hover:underline font-medium'}
      >
        {tag}
      </button>
    )
    lastIndex = matchStart + prefix.length + tag.length
  }
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex))
  }

  return <span className={className}>{parts}</span>
}

export default HashtagText